<template>
  <Menu
    :selectedKeys="selectedKeys"
    :defaultSelectedKeys="defaultSelectedKeys"
    :mode="mode"
    :openKeys="getOpenKeys"
    :inlineIndent="inlineIndent"
    :theme="theme"
    @open-change="handleOpenChange"
    :class="getMenuClass"
    @click="handleMenuClick"
    :subMenuOpenDelay="0.2"
    v-bind="getInlineCollapseOptions"
  >
    <template v-for="item in items" :key="item.path">
      <BasicSubMenuItem :item="item" :theme="theme" :isHorizontal="isHorizontal" />
    </template>
  </Menu>
</template>
<script lang="ts">
  import type { MenuState } from './types';
  import { computed, defineComponent, unref, reactive, watch, toRefs, ref } from 'vue';
  import { Menu } from 'ant-design-vue';
  import BasicSubMenuItem from './components/BasicSubMenuItem.vue';
  import { MenuModeEnum, MenuTypeEnum } from '@/enums/menuEnum';
  import { useOpenKeys } from './useOpenKeys';
  import { RouteLocationNormalizedLoaded, useRouter } from 'vue-router';
  import { isFunction } from 'xmgoing-utils';
  import { basicProps } from './props';
  import { useMenuSetting } from '@/hooks/setting/useMenuSetting';
  import { REDIRECT_NAME } from '@/router/constant';
  import { useDesign } from '@/hooks/web/useDesign';
  import { getCurrentParentPath } from '@/router/menus';
  import { listenerRouteChange } from '@/logics/mitt/routeChange';
  import { getAllParentPath } from '@/router/helper/menuHelper';
  import {getSelItem} from "@/utils";
  import {StorageUtils} from 'xmgoing-utils';
  import eventBus from '@/events/eventBus';
  import { useMultipleTabStore } from '@/store/modules/multipleTab';
  import { getMenuEntryKey } from '@/utils/buryingPoint';


  export default defineComponent({
    name: 'BasicMenu',
    components: {
      Menu,
      BasicSubMenuItem,
    },
    props: basicProps,
    emits: ['menuClick'],
    setup(props, { emit }) {
      const isClickGo = ref(false);
      const currentActiveMenu = ref('');
      const tabStore = useMultipleTabStore();

      const menuState = reactive<MenuState>({
        defaultSelectedKeys: [],
        openKeys: [],
        selectedKeys: [],
        collapsedOpenKeys: [],
      });

      const { prefixCls } = useDesign('basic-menu');
      const { items, mode, accordion } = toRefs(props);

      const { getCollapsed, getTopMenuAlign, getSplit } = useMenuSetting();

      const { currentRoute } = useRouter();

      const { handleOpenChange, setOpenKeys, getOpenKeys } = useOpenKeys(
        menuState,
        items,
        mode as any,
        accordion,
      );

      const getIsTopMenu = computed(() => {
        const { type, mode } = props;

        return (
          (type === MenuTypeEnum.TOP_MENU && mode === MenuModeEnum.HORIZONTAL) ||
          (props.isHorizontal && unref(getSplit))
        );
      });

      const getMenuClass = computed(() => {
        const align = props.isHorizontal && unref(getSplit) ? 'start' : unref(getTopMenuAlign);
        return [
          'BasicMenu',
          prefixCls,
          `justify-${align}`,
          {
            [`${prefixCls}__second`]: !props.isHorizontal && unref(getSplit),
            [`${prefixCls}__sidebar-hor`]: unref(getIsTopMenu),
          },
        ];
      });

      const getInlineCollapseOptions = computed(() => {
        const isInline = props.mode === MenuModeEnum.INLINE;

        const inlineCollapseOptions: { inlineCollapsed?: boolean } = {};
        if (isInline) {
          inlineCollapseOptions.inlineCollapsed = props.mixSider ? false : unref(getCollapsed);
        }
        return inlineCollapseOptions;
      });

      listenerRouteChange((route) => {
        if (route.name === REDIRECT_NAME) return;
        handleMenuChange(route);
        currentActiveMenu.value = route.meta?.currentActiveMenu as string;

        if (unref(currentActiveMenu)) {
          const activePath = unref(currentActiveMenu) as string;
          const targetObj: any = { target: null };
          getSelItem(activePath, unref(items), targetObj);
          menuState.selectedKeys = [
            targetObj.target ? getMenuEntryKey(targetObj.target) : activePath,
          ];
          setOpenKeys(activePath);
        }
      });

      !props.mixSider &&
        watch(
          () => props.items,
          () => {
            handleMenuChange();
          },
        );

      async function handleMenuClick({ key }: { key: string; keyPath: string[] }) {
        const targetObj:any={target:null};
        getSelItem(key,unref(items),targetObj);
        // 优先用菜单真实 path（含 paramPath），避免 key(入口 id) 被当成路由
        const navPath = (targetObj.target?.paramPath || targetObj.target?.path || key) as string;
        if (targetObj.target) {
          // ACL / 权限仍用 appOldId||id；侧栏消歧用唯一菜单 id
          StorageUtils.addSessionItem("curMenuId",targetObj.target.appOldId||targetObj.target.id);
          StorageUtils.addSessionItem("curMenuEntryId", targetObj.target.id);
          StorageUtils.addSessionItem("curMenuName",targetObj.target.name);
          if(targetObj.target.name){
            document.title=targetObj.target.name;
          }
          // 按 path 缓存标题，页签切换主题后仍可正确显示
          if (navPath && targetObj.target.name) {
            StorageUtils.addSessionItem(navPath, targetObj.target.name);
          }
          if (targetObj.target.path) {
            tabStore.bindTabMenuEntryId(targetObj.target.path, targetObj.target.id);
          }
          eventBus.emit('menuEntryChange', {
            entryId: targetObj.target.id,
            path: targetObj.target.path,
          });
        }
        if (navPath.startsWith('/big-screen')) {
          let Url = new URL(navPath, location.origin);
          const sessionId = StorageUtils.getSessionItem("sessionId")!;
          if (!import.meta.env.PROD) {
            Url.port = import.meta.env.VITE_APP_BIG_SCREEN;
            Url.searchParams.set("sessionId", sessionId);
          }

          window.open(Url.toString());

          return
        }

        if (navPath.startsWith('/jieSeriesDashboard')) {
          window.open(new URL(navPath, location.origin).toString(), '_blank');
          return;
        }

        //如果是首页设计页面
        if(window.appObj.analysisSet){
          return false;
        }
        const { beforeClickFn } = props;
        if (beforeClickFn && isFunction(beforeClickFn)) {
          const flag = await beforeClickFn(navPath);
          if (!flag) return;
        }
        emit('menuClick', navPath);

        isClickGo.value = true;
        menuState.selectedKeys = [key];
      }

      async function handleMenuChange(route?: RouteLocationNormalizedLoaded) {
        if (unref(isClickGo)) {
          isClickGo.value = false;
          return;
        }
        const path =
          (route || unref(currentRoute)).meta?.currentActiveMenu ||
          (route || unref(currentRoute)).path;
        setOpenKeys(path);
        if (unref(currentActiveMenu)) return;
        if (props.isHorizontal && unref(getSplit)) {
          const parentPath = await getCurrentParentPath(path);
          menuState.selectedKeys = [parentPath];
        } else {
          const targetObj: any = { target: null };
          getSelItem(path, unref(items), targetObj);
          if (targetObj.target) {
            menuState.selectedKeys = [getMenuEntryKey(targetObj.target)];
          } else {
            const parentPaths = await getAllParentPath(props.items, path);
            menuState.selectedKeys = parentPaths;
          }
        }
      }

      return {
        handleMenuClick,
        getInlineCollapseOptions,
        getMenuClass,
        handleOpenChange,
        getOpenKeys,
        ...toRefs(menuState),
      };
    },
  });
</script>
<style lang="less">
  @import './index.less';

</style>
